import React, { useState, useEffect, useRef } from 'react';
import { Briefcase, GraduationCap, Award, CheckCircle, Users, BookOpen, Shield, Send, Clock, MapPin } from 'lucide-react';

// Custom hook for scroll animation
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(element);
    return () => element && observer.unobserve(element);
  }, [threshold]);

  return [ref, isVisible];
};

const VacancySection = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [cardsRef, cardsVisible] = useScrollAnimation(0.1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    qualified: '',
    experience: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_name: formData.name,
          email: formData.email,
          phone: formData.phone,
          student_name: 'Job Application',
          exam_board: formData.subject,
          tier: formData.qualified,
          message: `Teaching Experience: ${formData.experience}\n\n${formData.message}`,
          source: 'vacancy_application'
        })
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitted(true);
    }

    setIsSubmitting(false);
  };

  const positions = [
    {
      title: "Online GCSE Maths Teacher",
      type: "Part-time / Flexible",
      icon: BookOpen,
      requirements: [
        "QTS (Qualified Teacher Status) preferred",
        "Non-QTS candidates welcome to apply",
        "Strong subject knowledge in GCSE/IGCSE Maths",
        "Experience teaching Higher & Foundation tiers",
        "Excellent communication skills",
        "Reliable internet connection",
        "Graphics tablet with pen for interactive teaching"
      ],
      ideal: "Passionate about helping students achieve their potential"
    },
    {
      title: "Online GCSE Science Teacher",
      type: "Part-time / Flexible",
      icon: Award,
      requirements: [
        "QTS (Qualified Teacher Status) preferred",
        "Non-QTS candidates welcome to apply",
        "Expertise in Biology, Chemistry, or Physics",
        "GCSE/IGCSE teaching experience",
        "Ability to explain complex concepts simply",
        "Patient and encouraging teaching style",
        "Graphics tablet with pen for interactive teaching"
      ],
      ideal: "Dedicated to making science accessible and engaging"
    }
  ];

  if (submitted) {
    return (
      <section id="vacancies" style={{
        padding: '6rem 1.5rem',
        background: 'var(--cream)',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '500px',
          margin: '0 auto',
          background: 'white',
          padding: '3rem',
          borderRadius: '16px',
          boxShadow: 'var(--shadow-medium)',
          border: '2px solid #C5A059'
        }}>
          <CheckCircle size={60} color="#C5A059" style={{ marginBottom: '1.5rem' }} />
          <h3 style={{ 
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '1.75rem',
            color: '#0F172A',
            marginBottom: '1rem'
          }}>
            Application Received!
          </h3>
          <p style={{ color: '#334155', lineHeight: 1.6 }}>
            Thank you for your interest in joining Abba Academy. We will review your application and be in touch within 5 working days.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="vacancies" style={{
      padding: '6rem 1.5rem',
      background: 'var(--cream)'
    }}>
      <div className="container" style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div 
          ref={headerRef}
          className={`fade-up ${headerVisible ? 'visible' : ''}`}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: '#0F172A',
            color: '#C5A059',
            padding: '10px 24px',
            borderRadius: '4px',
            marginBottom: '1.5rem',
            fontSize: '0.85rem',
            fontWeight: 600,
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            <Briefcase size={16} />
            Join Our Team
          </div>
          
          <h2 style={{ 
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(2rem, 4vw, 2.75rem)',
            color: '#0F172A',
            marginBottom: '1rem',
            lineHeight: 1.2
          }}>
            We're Hiring Exceptional
            <br />
            <span style={{ color: '#C5A059' }}>Maths & Science Tutors</span>
          </h2>
          
          <p style={{ 
            color: '#334155', 
            fontSize: '1.1rem',
            maxWidth: '650px',
            margin: '0 auto',
            lineHeight: 1.7
          }}>
            Join Abba Academy and help students achieve their GCSE goals. We're looking for passionate educators who share our commitment to academic excellence.
          </p>
        </div>

        {/* Position Cards */}
        <div 
          ref={cardsRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '2rem',
            marginBottom: '4rem'
          }}
        >
          {positions.map((position, index) => {
            const IconComponent = position.icon;
            return (
              <div
                key={index}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '2rem',
                  boxShadow: 'var(--shadow-soft)',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  opacity: cardsVisible ? 1 : 0,
                  transform: cardsVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  transitionDelay: `${index * 0.2}s`
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    background: 'rgba(197, 160, 89, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <IconComponent size={24} color="#C5A059" />
                  </div>
                  <div>
                    <h3 style={{ 
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontSize: '1.25rem',
                      color: '#0F172A',
                      margin: 0
                    }}>
                      {position.title}
                    </h3>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginTop: '0.25rem'
                    }}>
                      <Clock size={14} color="#64748B" />
                      <span style={{ color: '#64748B', fontSize: '0.85rem' }}>
                        {position.type}
                      </span>
                      <MapPin size={14} color="#64748B" style={{ marginLeft: '0.5rem' }} />
                      <span style={{ color: '#64748B', fontSize: '0.85rem' }}>
                        Remote
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ 
                    fontWeight: 600, 
                    color: '#0F172A', 
                    marginBottom: '0.75rem',
                    fontSize: '0.9rem'
                  }}>
                    Requirements:
                  </p>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0, 
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                    {position.requirements.map((req, idx) => (
                      <li 
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.5rem',
                          fontSize: '0.9rem',
                          color: '#334155'
                        }}
                      >
                        <CheckCircle size={16} color="#C5A059" style={{ flexShrink: 0, marginTop: '2px' }} />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{
                  background: 'rgba(197, 160, 89, 0.1)',
                  border: '1px solid rgba(197, 160, 89, 0.3)',
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                  fontSize: '0.9rem',
                  color: '#0F172A',
                  fontStyle: 'italic'
                }}>
                  <strong style={{ color: '#C5A059' }}>Ideal candidate:</strong> {position.ideal}
                </div>
              </div>
            );
          })}
        </div>

        {/* Important Notes */}
        <div style={{
          background: '#0F172A',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '4rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Shield size={24} color="#C5A059" />
              <h4 style={{ color: 'white', margin: 0, fontSize: '1.1rem' }}>DBS Check Required</h4>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>
              All tutors must hold or be willing to obtain an enhanced DBS check. We prioritize safeguarding our students.
            </p>
          </div>
          
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <GraduationCap size={24} color="#C5A059" />
              <h4 style={{ color: 'white', margin: 0, fontSize: '1.1rem' }}>Non-QTS Candidates</h4>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>
              University undergraduates and graduates who achieved <strong style={{ color: '#C5A059' }}>Grade 8 or 9</strong> in Maths and Science GCSEs are welcome to apply. Must demonstrate strong academic ability.
            </p>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C5A059" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="14" rx="2" ry="2"></rect>
                <line x1="12" y1="17" x2="12" y2="21"></line>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <path d="M7 8l3 3-3 3"></path>
                <line x1="14" y1="11" x2="17" y2="11"></line>
              </svg>
              <h4 style={{ color: 'white', margin: 0, fontSize: '1.1rem' }}>Graphics Tablet Required</h4>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>
              All Abba Academy tutors must have a <strong style={{ color: '#C5A059' }}>graphics tablet with graphics pen</strong> to support interactive online teaching. This enables real-time annotation and problem-solving during lessons.
            </p>
          </div>
        </div>

        {/* Application Form */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2.5rem',
          boxShadow: 'var(--shadow-medium)',
          border: '2px solid rgba(197, 160, 89, 0.3)'
        }}>
          <h3 style={{ 
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '1.5rem',
            color: '#0F172A',
            marginBottom: '0.5rem',
            textAlign: 'center'
          }}>
            Apply Now
          </h3>
          <p style={{ 
            color: '#64748B', 
            textAlign: 'center', 
            marginBottom: '2rem',
            fontSize: '0.95rem'
          }}>
            Complete the form below and we'll be in touch within 5 working days
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.25rem',
              marginBottom: '1.25rem'
            }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0F172A', fontSize: '0.9rem' }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(15, 23, 42, 0.15)',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0F172A', fontSize: '0.9rem' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(15, 23, 42, 0.15)',
                    fontSize: '1rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0F172A', fontSize: '0.9rem' }}>
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(15, 23, 42, 0.15)',
                    fontSize: '1rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="07XXX XXXXXX"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0F172A', fontSize: '0.9rem' }}>
                  Subject Area *
                </label>
                <select
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(15, 23, 42, 0.15)',
                    fontSize: '1rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="">Select subject...</option>
                  <option value="Maths">GCSE Maths</option>
                  <option value="Biology">GCSE Biology</option>
                  <option value="Chemistry">GCSE Chemistry</option>
                  <option value="Physics">GCSE Physics</option>
                  <option value="Multiple Sciences">Multiple Sciences</option>
                  <option value="Maths & Sciences">Maths & Sciences</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0F172A', fontSize: '0.9rem' }}>
                  Qualified Teacher Status *
                </label>
                <select
                  required
                  value={formData.qualified}
                  onChange={(e) => setFormData({...formData, qualified: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(15, 23, 42, 0.15)',
                    fontSize: '1rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="">Select status...</option>
                  <option value="QTS">Yes - I have QTS</option>
                  <option value="PGCE">PGCE (non-QTS)</option>
                  <option value="Graduate">Graduate (Grade 8/9 in GCSE)</option>
                  <option value="Undergraduate">University Student (Grade 8/9 in GCSE)</option>
                  <option value="Other">Other qualification</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0F172A', fontSize: '0.9rem' }}>
                  Teaching Experience *
                </label>
                <select
                  required
                  value={formData.experience}
                  onChange={(e) => setFormData({...formData, experience: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(15, 23, 42, 0.15)',
                    fontSize: '1rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="">Select experience...</option>
                  <option value="None">No formal experience</option>
                  <option value="1-2 years">1-2 years tutoring</option>
                  <option value="3-5 years">3-5 years</option>
                  <option value="5+ years">5+ years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0F172A', fontSize: '0.9rem' }}>
                Tell us about yourself *
              </label>
              <textarea
                required
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(15, 23, 42, 0.15)',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                placeholder="Share your teaching experience, qualifications, why you want to join Abba Academy, and your availability..."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-premium"
              style={{
                width: '100%',
                padding: '1rem 2rem',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Submitting...' : (
                <>
                  <Send size={18} />
                  Submit Application
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default VacancySection;
